import React from "react";
import { useNavigate } from "react-router-dom";
import "./PrivacyPolicyPage.css";
import "./DeleteAccountPage.css";

const SUPPORT_EMAIL = "support@investxapp.com";

const STEPS = [
  "Open the InvestX app.",
  "Tap the settings (gear) icon on Home, Market, or Invest.",
  "On the Account screen, open Settings and select Delete Account.",
  "Confirm your decision.",
  "Your account and data are deleted immediately.",
];

const DELETED_DATA = [
  "InvestX account",
  "Portfolio data",
  "Transactions",
  "Watchlists",
  "User preferences",
  "AI-related account data",
  "Saved settings",
];

const FAQ_ITEMS = [
  {
    question: "I can't access my account.",
    answer:
      "If you're unable to log in but would still like your account deleted, please contact our support team.",
  },
  {
    question: "How long does deletion take?",
    answer:
      "Account deletion is processed immediately after confirmation. Some legally required records, if any, may be retained for the period required by law.",
  },
];

const TrashIcon = () => (
  <svg
    className="delete-account-page__icon-svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    aria-hidden
  >
    <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
  </svg>
);

const ShieldIcon = () => (
  <svg
    className="delete-account-page__icon-svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    aria-hidden
  >
    <path d="M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6l-8-3Z" />
  </svg>
);

const LegalFooter = ({ navigate }) => (
  <div className="landing-cobalt-pagefoot">
    <div className="landing-page-width landing-page-width--ruled-top">
      <div className="landing-cobalt-pagefoot__inner">
        <span>© {new Date().getFullYear()} InvestX</span>
        <div className="landing-cobalt-pagefoot__links">
          <button type="button" onClick={() => navigate("/")}>
            Home
          </button>
          <button type="button" onClick={() => navigate("/privacy")}>
            Privacy
          </button>
          <button type="button" onClick={() => navigate("/support")}>
            Support
          </button>
          <button type="button" onClick={() => navigate("/terms")}>
            Terms
          </button>
          <button type="button" onClick={() => navigate("/delete-account")}>
            Delete account
          </button>
        </div>
      </div>
    </div>
  </div>
);

const DeleteAccountPage = () => {
  const navigate = useNavigate();

  return (
    <main className="landing-legal-page delete-account-page">
      <div className="landing-page-width">
        <article className="landing-legal-page__article delete-account-page__article">
          <header className="delete-account-page__hero">
            <p className="landing-legal-page__effective">Last updated: July 2026</p>
            <h1 className="landing-legal-page__title">
              Delete Your InvestX Account
            </h1>
            <p className="delete-account-page__subtitle">
              You can permanently delete your InvestX account directly from
              within the application. This page explains the process and what
              happens to your data.
            </p>
          </header>

          <section className="delete-account-page__section">
            <h2>How to Delete Your Account</h2>
            <ol className="delete-account-page__steps">
              {STEPS.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </section>

          <section className="delete-account-page__card">
            <div className="delete-account-page__card-header">
              <span className="delete-account-page__icon delete-account-page__icon--trash">
                <TrashIcon />
              </span>
              <h2>Data That Will Be Deleted</h2>
            </div>
            <ul>
              {DELETED_DATA.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="delete-account-page__card">
            <div className="delete-account-page__card-header">
              <span className="delete-account-page__icon delete-account-page__icon--shield">
                <ShieldIcon />
              </span>
              <h2>Data That May Be Retained</h2>
            </div>
            <p>
              Certain information may be retained only where required by
              applicable law, for fraud prevention, dispute resolution,
              security, or compliance with legal obligations.
            </p>
          </section>

          <section className="delete-account-page__section">
            <h2>Need Help?</h2>
            <div className="delete-account-page__faq">
              {FAQ_ITEMS.map((item) => (
                <details key={item.question} className="delete-account-page__faq-item">
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </section>

          <section className="delete-account-page__contact">
            <h2>Contact Support</h2>
            <p>
              If you experience any issues deleting your account, please contact
              our support team.
            </p>
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </section>
        </article>
      </div>

      <LegalFooter navigate={navigate} />
    </main>
  );
};

export default DeleteAccountPage;
